import React from 'react';
import { render } from 'src/lib/utils';
import { RequestOutcome } from 'src/lib/services/api/request-api/request-client';
import { requestOutcomeStatus } from 'src/lib/services/api/request-api';
import { decorateMedia } from 'src/features/media';
import { totalLengthToString } from 'src/features/requests/request-fields/request-field.helper';

type DecoratedRequestOutcome = {
  raw: RequestOutcome;
  id: string;
  latestRevision?: RequestOutcome['latest_revision'];
  revisionPreview?: ReturnType<typeof decorateMedia>;
  canCreateFirstContact: boolean;
  revisionPreviewImage?: string;
  hasRevisions: boolean;
  needsReedit: boolean;
  isQueued: boolean;
  isEditing: boolean;
  canMoveToEditing: boolean;
  isArchived: boolean;
  canArchive: boolean;
  canRestore: boolean;
  totalLength: string | undefined;
  canRequestAttention: boolean;
  needsAttention: boolean;
  attentionText: string;
};

const decorateRequestOutcome = (outcome?: RequestOutcome): DecoratedRequestOutcome | undefined => {
  if (!outcome) {
    return outcome;
  }

  const raw = outcome;

  const id = raw.id,
    hasRevisions = !!raw.revisions_count,
    isQueued = raw.status === requestOutcomeStatus.queued,
    isArchived = raw.status === requestOutcomeStatus.archived,
    isEditing = raw.status === requestOutcomeStatus.currentlyEditing,
    canMoveToEditing = isQueued,
    canCreateFirstContact = isQueued && !hasRevisions,
    latestRevision = raw.latest_revision,
    revisionPreview = latestRevision?.video ? decorateMedia(latestRevision?.video) : undefined,
    revisionPreviewImage = revisionPreview?.thumbnail || revisionPreview?.poster,
    needsReedit = !!raw.requested_reedit_at,
    canArchive = raw.status === requestOutcomeStatus.queued && !latestRevision,
    canRestore = raw.status === requestOutcomeStatus.archived,
    totalLength = totalLengthToString(raw.total_length),
    needsAttention = !!raw.requested_attention_at,
    // #2532: a "Needs Re-edit" outcome is technically Queued but carries prior
    // revisions; flagging attention re-opens the customer's editable form and a
    // submit would wipe the delivered video + revisions. Block it mid re-edit.
    canRequestAttention = (isQueued && !needsReedit) || isEditing,
    attentionText = raw.attention_text ?? '';

  return {
    raw,
    id,
    latestRevision,
    revisionPreview,
    canCreateFirstContact,
    revisionPreviewImage,
    hasRevisions,
    needsReedit,
    isQueued,
    isEditing,
    canMoveToEditing,
    isArchived,
    canArchive,
    canRestore,
    totalLength,
    canRequestAttention,
    needsAttention,
    attentionText,
  };
};

const DecoratedRequestOutcomeContext = React.createContext<DecoratedRequestOutcome | undefined>(
  undefined,
);

const DecoratedRequestOutcomeContextProvider = ({
  outcome,
  children,
}: {
  outcome: RequestOutcome;
  children: React.ReactNode | ((outcome: DecoratedRequestOutcome) => React.ReactNode);
}) => {
  const value = decorateRequestOutcome(outcome);

  return (
    <DecoratedRequestOutcomeContext.Provider value={value!}>
      {render(children, value)}
    </DecoratedRequestOutcomeContext.Provider>
  );
};

const useDecoratedRequestOutcomeContext = () => {
  const context = React.useContext(DecoratedRequestOutcomeContext);

  if (!context) {
    throw new Error(
      'useDecoratedRequestOutcomeContext must be used within a RequestOutcomeContextProvider',
    );
  }

  return context;
};

export {
  decorateRequestOutcome,
  DecoratedRequestOutcomeContextProvider,
  useDecoratedRequestOutcomeContext,
};
